import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	ItemRoll,
	PurchaseOrder,
	PurchaseOrderItem,
	UpsertItemRollParams,
} from "../models/RollsReceived";

export interface GetPurchaseOrdersParams {
	search?: string | null;
	onlyActives?: boolean;
	signal?: AbortSignal;
}

export const getPurchaseOrders = async ({
	search = null,
	onlyActives = false,
	signal,
}: GetPurchaseOrdersParams): Promise<PurchaseOrder[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "id", value: null },
		{ name: "search", value: search },
		{ name: "onlyActives", value: onlyActives },
		{ name: "includeItems", value: false },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[GetPurchaseOrders]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching purchase orders");
	}
	return get(resp, "data.tables[0].rows", []) as PurchaseOrder[];
};

export interface GetPurchaseOrderDetailsParams {
	purchaseOrderId: number;
	signal?: AbortSignal;
}

export const getPurchaseOrderDetails = async ({
	purchaseOrderId,
	signal,
}: GetPurchaseOrderDetailsParams): Promise<PurchaseOrderItem[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "id", value: null },
		{ name: "onlyActives", value: false },
		{ name: "purchaseOrderId", value: purchaseOrderId },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[GetPurchaseOrderDetails]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching purchase order items");
	}
	return get(resp, "data.tables[0].rows", []) as PurchaseOrderItem[];
};

export interface GetItemRollsParams {
	purchaseOrderItemId: number;
	signal?: AbortSignal;
}

export const getItemRolls = async ({
	purchaseOrderItemId,
	signal,
}: GetItemRollsParams): Promise<ItemRoll[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "purchaseOrderItemId", value: purchaseOrderItemId },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[GetPurchaseOrderItemRolls]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching received rolls");
	}
	return get(resp, "data.tables[0].rows", []) as ItemRoll[];
};

export const upsertItemRoll = async (
	params: UpsertItemRollParams,
): Promise<number> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: params.Id ?? null },
		{ name: "Name", value: params.Name },
		{ name: "PurchaseOrderItemId", value: params.PurchaseOrderItemId },
		{ name: "Hardness", value: params.Hardness },
		{
			name: "SupplierInvoiceNumber",
			value: params.SupplierInvoiceNumber ?? null,
		},
	];

	const resp = await apiService.callV2(
		"[ROLLS].[UpsertPurchaseOrderItemRoll]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error saving received roll");
	}
	return get(resp, "data.tables[0].rows[0].Id", 0) as number;
};

export const removeItemRoll = async (id: number): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "Id", value: id }];

	const resp = await apiService.callV2(
		"[ROLLS].[RemovePurchaseOrderItemRoll]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error removing received roll");
	}
};
